// @ts-nocheck
/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unused-vars, @typescript-eslint/no-unsafe-return, no-empty, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import { TOKEN } from './token';
import { cloneDeep, merge, isPlainObject } from 'lodash';
import { getCachedToken } from './auth';
import { isBloomberg } from '../bloomberg';
import { getEnv } from './common';
import { FetchAuxOptions, ErrorResponse } from '../../types';
import { getOpenHouseToken } from '../user';
import { unauthorizedRedirectFallback } from 'util/unauthorizedRedirectFallback';

export const DEFAULT_OPTS = Object.freeze({
  method: 'GET',
  crossDomain: true,
  Origin: '*',
  'X-Requested-With': 'XMLHttpRequest',
  headers: Object.freeze({
    'x-json-web-token': TOKEN,
    'Content-Type': 'application/json',
    'App-Type': isBloomberg() ? 'bloomberg' : 'web',
  }),
});

type BearerToken = `Bearer ${string}`;
type AuthHeaderValue = BearerToken;
type OpenHouseTokenHeader = string;

interface AuthHeaders {
  Authorization?: AuthHeaderValue;
  'x-open-house-token'?: OpenHouseTokenHeader;
}

export function getAuthHeader(sgBearerToken = getCachedToken()): {
  headers: AuthHeaders;
} {
  const headers: AuthHeaders = {};
  const ohToken = getOpenHouseToken();
  if (ohToken != null) {
    headers['x-open-house-token'] = ohToken;
  }
  if (sgBearerToken != null) {
    headers.Authorization = `Bearer ${sgBearerToken}`;
  }
  return { headers };
}

export function getVersionHeader() {
  return import.meta.env.VITE_VERSION_NUM == null
    ? {}
    : { headers: { Version: `${import.meta.env.VITE_VERSION_NUM}` } };
}

export function getStreamApiBaseURL(
  auxiliaryOptions: FetchAuxOptions = {},
): string {
  let result = auxiliaryOptions.host ?? import.meta.env.VITE_SPOTGAMMA_API;
  if (
    getEnv() === 'development' &&
    (auxiliaryOptions?.local || import.meta.env.VITE_ALL_LOCAL_FETCH === 'true')
  ) {
    result = import.meta.env.VITE_LOCAL_API ?? result;
  }
  return result;
}

// Fetch raw response.  Don't parse JSON
export async function fetchRawAPI<Rsp = any>(
  endpoint: string,
  auxiliaryOptions: FetchAuxOptions = {},
): Promise<NoInfer<Rsp>> {
  try {
    const currentHost = getStreamApiBaseURL(auxiliaryOptions);
    const url = `${currentHost}/${endpoint}`;
    const opts = merge(
      { ...cloneDeep(DEFAULT_OPTS) },
      getAuthHeader(),
      getVersionHeader(),
      auxiliaryOptions,
    );
    const response = await fetch(url, opts);
    if (response.status >= 300) {
      if (response.status === 401) {
        unauthorizedRedirectFallback();
      }

      console.error(`${url} returned status: ${response.status}`);
      let text = null;
      let json = null;
      try {
        text = await response.text();
        json = JSON.parse(text);
      } catch (e) {}
      return {
        error: text ?? 'There was an error. Please try again.',
        json: json,
        status: response.status,
        response,
      };
    }
    return response;
  } catch (err: any) {
    const defaultMaxRetries = err.message?.includes('Failed to fetch') ? 1 : 0;
    const retryCounter = auxiliaryOptions.retryCounter ?? 0;
    if (retryCounter < (auxiliaryOptions?.maxRetries ?? defaultMaxRetries)) {
      return fetchRawAPI(endpoint, {
        ...auxiliaryOptions,
        retryCounter: retryCounter + 1,
      });
    }

    throw err;
  }
}

export async function fetchAPI(
  endpoint: string,
  auxiliaryOptions?: FetchAuxOptions,
): Promise<any> {
  let response = null;
  try {
    response = await fetchRawAPI(endpoint, auxiliaryOptions);
    return response.error
      ? response
      : auxiliaryOptions?.buffer
        ? await response.arrayBuffer()
        : await response.json();
  } catch (err: any) {
    return { error: err, status: response?.status, response };
  }
}

export const getPollingHeader = (interval: number) => ({
  headers: { 'Polling-Interval-ms': `${interval}` },
});

export const encodeURIJson = (payload: any) =>
  encodeURIComponent(JSON.stringify(payload));

export const isErrorResponse = (val: any): val is ErrorResponse =>
  isPlainObject(val) && val.error != null;
